
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { 
  Table, 
  TableHeader, 
  TableRow, 
  TableHead, 
  TableBody, 
  TableCell 
} from '@/components/ui/table';
import { 
  Calendar, 
  FileText, 
  Search, 
  Plus, 
  ArrowRight, 
  Edit 
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { FollowUpReport, PostTreatmentReport } from '@/types/followUpReport';
import PatientSelectorModal from '@/components/doctor/PatientSelectorModal';

const FollowUpReportsPage = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isPatientSelectorOpen, setIsPatientSelectorOpen] = useState(false);
  const [tab, setTab] = useState('follow-ups');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  // Mock data - in a real app, this would come from an API
  const followUpReports: FollowUpReport[] = [
    {
      id: '1',
      patientId: 1001,
      patientName: 'John Doe',
      treatmentId: 5001,
      treatmentName: 'Dental Implant',
      followUpDate: '2025-04-20',
      summary: 'Patient recovery is progressing well. No signs of infection.',
      status: 'pending',
      requiresReEvaluation: false,
      attachments: [],
      createdAt: '2025-04-13',
      updatedAt: '2025-04-13'
    },
    {
      id: '2',
      patientId: 1002,
      patientName: 'Jane Smith',
      treatmentId: 5002,
      treatmentName: 'Root Canal',
      followUpDate: '2025-04-18',
      summary: 'Patient reports mild discomfort. Prescribed painkillers.',
      status: 'completed',
      requiresReEvaluation: true,
      attachments: [],
      createdAt: '2025-04-10',
      updatedAt: '2025-04-12'
    }
  ];
  
  const postTreatmentReports: PostTreatmentReport[] = [
    {
      id: '1',
      patientId: 1001,
      patientName: 'John Doe',
      treatmentPlanId: 3001,
      treatmentName: 'Dental Implant',
      startDate: '2025-03-01',
      endDate: '2025-04-01',
      isCompleted: true,
      doctorNotes: 'Treatment completed successfully.',
      requiresFollowUp: true,
      finalXRays: [],
      beforeAfterImages: { before: [], after: [] },
      medicalReports: [],
      invoices: [],
      prescriptions: [],
      submittedAt: '2025-04-05',
      lastModified: '2025-04-05',
      status: 'submitted'
    }
  ];
  
  // Filter reports based on search and status
  const filteredFollowUps = followUpReports.filter(report => {
    const matchesSearch = report.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.treatmentName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || report.status === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  const filteredPostTreatments = postTreatmentReports.filter(report => {
    const matchesSearch = report.patientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      report.treatmentName.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'completed' && report.isCompleted) || 
      (statusFilter === 'pending' && !report.isCompleted);
    return matchesSearch && matchesStatus;
  });
  
  const handlePatientSelected = (patientId) => {
    navigate(`/doctor-area/follow-up-reports/${patientId}/create`);
    setIsPatientSelectorOpen(false);
  };
  
  const handleCreate = () => {
    setIsPatientSelectorOpen(true);
  };
  
  const handleEdit = (reportId) => {
    toast({
      title: "Edit function",
      description: "Edit functionality coming soon"
    });
  };
  
  const getStatusBadge = (status) => {
    if (status === 'pending') {
      return <Badge variant="warning">Pending</Badge>;
    } else if (status === 'completed') {
      return <Badge variant="success">Completed</Badge>;
    } else {
      return <Badge>{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl font-bold">Follow-up Reports</h1>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Create Report
        </Button>
      </div>
      
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <CardTitle>Reports</CardTitle>
            <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto">
              <TabsList>
                <TabsTrigger value="follow-ups">Follow-ups</TabsTrigger>
                <TabsTrigger value="post-treatments">Post-Treatment</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by patient or treatment name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <div className="w-full sm:w-[200px]">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          {tab === 'follow-ups' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Follow-up Date</TableHead>
                  <TableHead>Treatment</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredFollowUps.length > 0 ? filteredFollowUps.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>{report.patientName.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span>{report.patientName}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        {new Date(report.followUpDate).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>{report.treatmentName}</TableCell>
                    <TableCell>{getStatusBadge(report.status)}</TableCell>
                    <TableCell>{new Date(report.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleEdit(report.id)}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/doctor-area/case-file/${report.patientId}`)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No follow-up reports found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
          
          {tab === 'post-treatments' && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Patient</TableHead>
                  <TableHead>Treatment</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPostTreatments.length > 0 ? filteredPostTreatments.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Avatar className="h-8 w-8">
                          <AvatarFallback>{report.patientName.charAt(0)}</AvatarFallback>
                        </Avatar>
                        <span>{report.patientName}</span>
                      </div>
                    </TableCell>
                    <TableCell>{report.treatmentName}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-muted-foreground" />
                        <span>{new Date(report.startDate).toLocaleDateString()} - {new Date(report.endDate).toLocaleDateString()}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {report.isCompleted ? <Badge variant="success">Completed</Badge> : <Badge variant="warning">In Progress</Badge>}
                    </TableCell>
                    <TableCell>{new Date(report.submittedAt).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/doctor-area/post-treatment/${report.patientId}`)}
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => navigate(`/doctor-area/case-file/${report.patientId}`)}
                        >
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No post-treatment reports found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      
      <PatientSelectorModal
        open={isPatientSelectorOpen}
        onClose={() => setIsPatientSelectorOpen(false)}
        onPatientSelect={handlePatientSelected}
        title="Select Patient for Follow-up Report"
      />
    </div>
  );
};

export default FollowUpReportsPage;
